import { getStorage } from '@syntra/ui/hooks/use-local-storage';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  AppState,
  useAuth0,
  Auth0Provider,
  LogoutOptions,
  PopupLoginOptions,
} from '@auth0/auth0-react';

import { setAuthReady, setJwtToHeaders, clearJwtFromHeaders } from 'src/utils/axios';

import { getJwtToken } from 'src/api/auth';
import { useGetUserAccount } from 'src/domains/user-account/api';
import { AUTH0_API, LOCAL_STORAGE_KEYS } from 'src/config-global';

import { AuthContext } from './auth-context';
import { setSession, isValidToken } from './utils';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

function AuthProviderWrapper({ children }: Props) {
  const {
    isAuthenticated,
    user,
    isLoading,
    loginWithRedirect,
    loginWithPopup,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [popupClick, setPopupClick] = useState(true);
  const [jwtReady, setJwtReady] = useState(false);
  const [loading, setLoading] = useState(true);

  // Use SWR hook for fetching user account - only fetch when JWT is ready
  const {
    userAccount: extraUserInfo,
    userAccountLoading,
    userAccountError,
    refreshUserAccount,
  } = useGetUserAccount(isAuthenticated && jwtReady);

  const initUserTokenToSession = useCallback(async () => {
    try {
      const accessToken = getStorage(LOCAL_STORAGE_KEYS.auth);
      if (accessToken && isValidToken(accessToken)) {
        console.info('Token is already validated');
        setJwtToHeaders(accessToken); // This now also calls setAuthReady(true)
        setJwtReady(true);
        setLoading(false);
        return;
      }

      clearJwtFromHeaders();
      const token = await getAccessTokenSilently();
      const { jwt } = await getJwtToken(token);
      setJwtToHeaders(jwt); // This now also calls setAuthReady(true)
      setSession(jwt);
      setJwtReady(true);
      setLoading(false);
    } catch (error) {
      console.error('Error initializing user token:', error);
      setLoading(false);
      setJwtReady(false);
      setAuthReady(false);
    }
  }, [getAccessTokenSilently]);

  // LOGIN
  const handleLoginWithPopup = useCallback(
    async (options?: PopupLoginOptions) => {
      await loginWithPopup?.(options);
      setPopupClick(false);
      setLoading(false);
      await initUserTokenToSession();
    },
    [loginWithPopup, initUserTokenToSession]
  );

  // LOGOUT
  const handleLogout = useCallback(
    async (options?: LogoutOptions) => {
      setAuthReady(false); // Reset auth state on logout
      logout?.(options);
    },
    [logout]
  );

  // ----------------------------------------------------------------------

  const checkAuthenticated = isAuthenticated ? 'authenticated' : 'unauthenticated';

  const status = popupClick && isLoading ? 'loading' : checkAuthenticated;

  const handlePerformActionOrLogin = useCallback(
    async (e: React.MouseEvent, callback: () => void) => {
      e.preventDefault();
      if (isAuthenticated) {
        return callback();
      }
      return handleLoginWithPopup();
    },
    [handleLoginWithPopup, isAuthenticated]
  );

  useEffect(() => {
    if (isAuthenticated) {
      (async () => {
        await initUserTokenToSession();
      })();
    } else if (!isLoading) {
      // Only set loading to false if Auth0 has finished its initial check
      setLoading(false);
    }
  }, [initUserTokenToSession, isAuthenticated, isLoading]);

  // Handle loading state - consider both auth loading and user account loading
  useEffect(() => {
    if (isAuthenticated && jwtReady) {
      // If we're authenticated and JWT is ready, wait for user account
      setLoading(userAccountLoading);
    }
  }, [isAuthenticated, jwtReady, userAccountLoading]);

  // Log errors from SWR
  useEffect(() => {
    if (userAccountError) {
      console.error('User account fetch error (will retry automatically):', userAccountError);
    }
  }, [userAccountError]);

  const memoizedValue = useMemo(
    () => ({
      user: {
        ...user,
        displayName: user?.name,
        photoURL: user?.picture,
        role: status,
        id: extraUserInfo?.id ?? 0,
        ...extraUserInfo,
      },
      method: 'auth0',
      loading,
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      loginWithRedirect,
      loginWithPopup: handleLoginWithPopup,
      logout: handleLogout,
      handlePerformActionOrLogin,
      refreshUserAccount, // Expose refresh function
    }),
    [
      user,
      extraUserInfo,
      loading,
      status,
      loginWithRedirect,
      handleLoginWithPopup,
      handleLogout,
      handlePerformActionOrLogin,
      refreshUserAccount,
    ]
  );
  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

// ----------------------------------------------------------------------

export const AuthProvider = ({ children }: Props) => {
  const domain = AUTH0_API.domain ?? '';

  const clientId = AUTH0_API.clientId ?? '';

  const redirectUri = AUTH0_API.callbackUrl ?? '';

  const onRedirectCallback = useCallback((appState?: AppState) => {
    window.location.replace(appState?.returnTo || window.location.pathname);
  }, []);

  if (!(domain && clientId && redirectUri)) {
    return null;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      <AuthProviderWrapper>{children}</AuthProviderWrapper>
    </Auth0Provider>
  );
};
